import React from "react";

// Constants for random string generation
const RANDOM_BASE = 36;
const RANDOM_STRING_START = 2;
const RANDOM_STRING_LENGTH = 9;

// Constants for Spectrum loading
const SPECTRUM_CHECK_INTERVAL_MS = 50;
const SPECTRUM_LOAD_TIMEOUT_MS = 5000;

// Constants for hex color parsing
const HEX_RADIX = 16;
const HEX_SUBSTR_START_R = 1;
const HEX_SUBSTR_START_G = 3;
const HEX_SUBSTR_START_B = 5;
const HEX_SUBSTR_LENGTH = 2;
const PERCENT = 100;

// AB#11730: the account's brand colors lead the palette when the page has one.
function withBrandRows(rows) {
  return window.PiBrandPalette
    ? window.PiBrandPalette.withSpectrumPalette(rows)
    : rows;
}

// Color picker field component using Spectrum
export function ColorPickerField({
  field,
  onChange,
  value,
  showAlpha = false
}) {
  const [colorValue, setColorValue] = React.useState(
    value || (showAlpha ? "rgba(0, 0, 0, 1)" : "#000000")
  );
  const [spectrumLoaded, setSpectrumLoaded] = React.useState(false);
  const inputRef = React.useRef(null);
  const fieldId = React.useRef(
    `color-field-${Math.random()
      .toString(RANDOM_BASE)
      .substr(RANDOM_STRING_START, RANDOM_STRING_LENGTH)}`
  ).current;

  React.useEffect(() => {
    setColorValue(value || (showAlpha ? "rgba(0, 0, 0, 1)" : "#000000"));
  }, [value, showAlpha]);

  // Load Spectrum CSS
  React.useEffect(() => {
    if (!document.querySelector('link[href*="spectrum"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "/lib/spectrum/spectrum.min.css";
      document.head.appendChild(link);
    }
  }, []);

  // Load Spectrum JS and initialize (only once)
  React.useEffect(() => {
    if (!inputRef.current) return;

    function initializeSpectrum() {
      if (window.$ && window.$.fn.spectrum && inputRef.current) {
        const $input = window.$(inputRef.current);

        // Destroy existing instance if any
        if ($input.data("spectrum")) {
          $input.spectrum("destroy");
        }

        $input.spectrum({
          color: colorValue || (showAlpha ? "rgba(0, 0, 0, 1)" : "#000000"),
          showAlpha: showAlpha,
          preferredFormat: showAlpha ? "rgb" : "hex",
          showInput: true,
          clickoutFiresChange: true,
          showPalette: true,
          palette: withBrandRows([
            // Metronic Theme Colors - Primary, Success, Info, Warning, Danger, Dark
            [
              "#1b84ff",
              "#056ee9",
              "#17c653",
              "#04b440",
              "#7239ea",
              "#5014d0",
              "#f6c000",
              "#dead00",
              "#f8285a",
              "#d81a48",
              "#1e2129",
              "#111318"
            ],
            // Metronic Gray Scale - Light to Dark
            [
              "#ffffff",
              "#f9f9f9",
              "#f1f1f4",
              "#dbdfe9",
              "#c4cada",
              "#99a1b7",
              "#78829d",
              "#4b5675",
              "#252f4a",
              "#071437",
              "#000000"
            ],
            // Metronic Light Variants - Soft backgrounds
            [
              "#e9f3ff",
              "#dfffea",
              "#f8f5ff",
              "#fff8dd",
              "#ffeef3",
              "#f9f9f9",
              "#ffffff"
            ],
            // Metronic Active States - Deeper tones
            ["#056ee9", "#04b440", "#5014d0", "#dead00", "#d81a48", "#111318"],
            // Metronic Clarity Variants - Semi-transparent backgrounds (shown as solid preview)
            ["#e0f0ff", "#d0ffe5", "#f0e5ff", "#fff5d0", "#ffe5f0", "#e0e0e0"],
            // Additional Professional Colors - Complementary palette
            [
              "#0dcaf0",
              "#20c997",
              "#198754",
              "#ffc107",
              "#fd7e14",
              "#dc3545",
              "#6f42c1",
              "#e91e63"
            ]
          ]),
          change: function (color) {
            let colorString;
            if (showAlpha && color) {
              // Convert to rgba format
              const r = Math.round(color._r || 0);
              const g = Math.round(color._g || 0);
              const b = Math.round(color._b || 0);
              const a = typeof color._a !== "undefined" ? color._a : 1;
              colorString = `rgba(${r}, ${g}, ${b}, ${a})`;
            } else {
              colorString = color ? color.toHexString() : "#000000";
            }
            setColorValue(colorString);
            onChange(colorString);
          }
        });

        setSpectrumLoaded(true);
      }
    }

    // Check if Spectrum is already loaded
    if (window.$ && window.$.fn.spectrum) {
      initializeSpectrum();
    } else if (
      window.ScriptLoader &&
      window.ScriptLoader.scriptArrays &&
      window.ScriptLoader.scriptArrays.colorPicker
    ) {
      // Load via ScriptLoader
      window.ScriptLoader.loadScriptArray(
        window.ScriptLoader.scriptArrays.colorPicker,
        function () {
          initializeSpectrum();
        }
      );
    } else {
      // Fallback: try to load Spectrum directly
      const script = document.createElement("script");
      script.src = "/lib/spectrum/spectrum.min.js";
      script.onload = () => {
        // Wait for jQuery if needed
        const checkJQuery = setInterval(() => {
          if (window.$ && window.$.fn.spectrum) {
            clearInterval(checkJQuery);
            initializeSpectrum();
          }
        }, SPECTRUM_CHECK_INTERVAL_MS);

        // Timeout after specified duration
        setTimeout(() => {
          clearInterval(checkJQuery);
          if (!spectrumLoaded) {
            /*
             * Spectrum failed to load - fallback to HTML5 color input
             * Note: This is expected behavior when Spectrum library is unavailable
             */
          }
        }, SPECTRUM_LOAD_TIMEOUT_MS);
      };
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      if (inputRef.current && window.$ && window.$.fn.spectrum) {
        const $input = window.$(inputRef.current);
        if ($input.data("spectrum")) {
          $input.spectrum("destroy");
        }
      }
    };
  }, [onChange, showAlpha]); // Only initialize once, don't depend on colorValue

  // Update Spectrum color when value changes externally (without re-initializing)
  React.useEffect(() => {
    if (
      inputRef.current &&
      window.$ &&
      window.$.fn.spectrum &&
      spectrumLoaded
    ) {
      const $input = window.$(inputRef.current);
      if ($input.data("spectrum")) {
        const currentSpectrumColor = $input.spectrum("get");
        const currentColorString =
          showAlpha && currentSpectrumColor
            ? `rgba(${Math.round(currentSpectrumColor._r || 0)}, ${Math.round(
                currentSpectrumColor._g || 0
              )}, ${Math.round(currentSpectrumColor._b || 0)}, ${
                typeof currentSpectrumColor._a !== "undefined"
                  ? currentSpectrumColor._a
                  : 1
              })`
            : currentSpectrumColor
            ? currentSpectrumColor.toHexString()
            : "#000000";

        // Only update if the value prop changed and differs from current Spectrum color (avoid infinite loops)
        const valueToCompare =
          value || (showAlpha ? "rgba(0, 0, 0, 1)" : "#000000");
        if (currentColorString !== valueToCompare && valueToCompare) {
          $input.spectrum("set", valueToCompare);
        }
      }
    }
  }, [value, spectrumLoaded, showAlpha]); // Update when value prop changes

  // Helper function to convert colorValue to hex for HTML5 color input
  function getHexColor() {
    if (!colorValue) return "#000000";

    // If it's already hex, return it
    if (colorValue.startsWith("#")) {
      return colorValue;
    }

    // Convert rgba to hex
    if (colorValue.startsWith("rgba") || colorValue.startsWith("rgb")) {
      const rgbaMatch = colorValue.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1]).toString(HEX_RADIX).padStart(2, "0");
        const g = parseInt(rgbaMatch[2]).toString(HEX_RADIX).padStart(2, "0");
        const b = parseInt(rgbaMatch[3]).toString(HEX_RADIX).padStart(2, "0");
        return `#${r}${g}${b}`;
      }
    }

    return "#000000";
  }

  // Reads r, g, b and alpha from a hex or rgb()/rgba() string
  function parseColor(color) {
    if (typeof color !== "string") return null;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgba) {
      return {
        r: parseInt(rgba[1]),
        g: parseInt(rgba[2]),
        b: parseInt(rgba[3]),
        a: rgba[4] !== undefined ? parseFloat(rgba[4]) : 1
      };
    }
    const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
      const h =
        hex[1].length === 3 ? hex[1].replace(/./g, "$&$&") : hex[1];
      return {
        r: parseInt(h.substr(0, 2), HEX_RADIX),
        g: parseInt(h.substr(2, 2), HEX_RADIX),
        b: parseInt(h.substr(4, 2), HEX_RADIX),
        a: 1
      };
    }
    return null;
  }

  function setColor(newColor) {
    setColorValue(newColor);
    onChange(newColor);
    if (inputRef.current && window.$ && window.$.fn.spectrum) {
      const $input = window.$(inputRef.current);
      if ($input.data("spectrum")) {
        $input.spectrum("set", newColor);
      }
    }
  }

  const parsedColor = parseColor(colorValue);
  const opacityPercent = parsedColor
    ? Math.round(parsedColor.a * PERCENT)
    : PERCENT;

  // A visible opacity control, so transparency doesn't depend on finding the
  // alpha bar inside the Spectrum popup
  const opacityControl =
    showAlpha &&
    React.createElement(
      "div",
      {
        className: "color-field-opacity",
        style: {
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "6px"
        }
      },
      React.createElement(
        "label",
        { htmlFor: `${fieldId}_opacity`, style: { margin: 0 } },
        "Opacity"
      ),
      React.createElement("input", {
        type: "range",
        id: `${fieldId}_opacity`,
        className: "form-range",
        min: 0,
        max: PERCENT,
        step: 5,
        value: opacityPercent,
        "aria-valuetext": `${opacityPercent}%`,
        disabled: !parsedColor,
        style: { flex: 1 },
        onChange: (e) => {
          const base = parseColor(colorValue);
          if (!base) return;
          const alpha = parseInt(e.target.value, 10) / PERCENT;
          setColor(`rgba(${base.r}, ${base.g}, ${base.b}, ${alpha})`);
        }
      }),
      React.createElement(
        "span",
        { "aria-hidden": "true", style: { minWidth: "3em", textAlign: "right" } },
        `${opacityPercent}%`
      )
    );

  return React.createElement(
    "div",
    {
      className: "puck-color-field",
      "data-val": "false",
      "data-val-required": "false"
    },
    React.createElement(
      "label",
      { className: "puck-field-label" },
      field.label || "Color"
    ),
    React.createElement(
      "div",
      {
        className: "color-field-wrapper",
        style: { display: "flex", alignItems: "center", gap: "8px" },
        "data-val": "false"
      },
      React.createElement("input", {
        ref: inputRef,
        type: "text",
        id: fieldId,
        name: fieldId,
        value: colorValue,
        className: "form-control spectrum-color-select",
        placeholder:
          field.placeholder || (showAlpha ? "rgba(0, 0, 0, 1)" : "#000000"),
        style: { flex: 1 },
        "data-val": "false",
        "data-val-required": "false",
        onChange: (e) => {
          // Allow direct editing, but Spectrum will also handle changes
          const newValue = e.target.value;
          setColorValue(newValue);
          onChange(newValue);
        }
      }),
      React.createElement("input", {
        type: "color",
        name: `${fieldId}_color`,
        value: getHexColor(),
        "data-val": "false",
        "data-val-required": "false",
        onChange: (e) => {
          const hexColor = e.target.value;
          // If alpha is enabled, convert hex to rgba with full opacity
          const newColor = showAlpha
            ? (() => {
                const r = parseInt(
                  hexColor.substr(HEX_SUBSTR_START_R, HEX_SUBSTR_LENGTH),
                  HEX_RADIX
                );
                const g = parseInt(
                  hexColor.substr(HEX_SUBSTR_START_G, HEX_SUBSTR_LENGTH),
                  HEX_RADIX
                );
                const b = parseInt(
                  hexColor.substr(HEX_SUBSTR_START_B, HEX_SUBSTR_LENGTH),
                  HEX_RADIX
                );
                // Preserve existing alpha if available, otherwise use 1
                const existingAlpha =
                  colorValue && colorValue.match(/rgba?\([^)]+,\s*([\d.]+)\)/);
                const alpha = existingAlpha ? parseFloat(existingAlpha[1]) : 1;
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
              })()
            : hexColor;
          setColorValue(newColor);
          onChange(newColor);
          // Update Spectrum if available
          if (inputRef.current && window.$ && window.$.fn.spectrum) {
            const $input = window.$(inputRef.current);
            if ($input.data("spectrum")) {
              $input.spectrum("set", newColor);
            }
          }
        },
        style: {
          width: "40px",
          height: "38px",
          border: "1px solid #ced4da",
          borderRadius: "0.375rem",
          cursor: "pointer",
          padding: "2px",
          flexShrink: 0
        },
        title: showAlpha
          ? "Click to pick color (use Spectrum picker for transparency)"
          : "Click to pick color"
      })
    ),
    opacityControl
  );
}
